import { format, parseISO, subYears } from 'date-fns';
import { useMemo } from 'react';
import { usePayment } from '$api-clients/hooks';
import {
  buildReportingSummaryPerformanceQuery,
  formatAbbreviatedPrice,
  formatPercentage,
  getPriorReportingPeriodDateRange,
  getReportingPeriodDateRange,
  isReportingPeriodAllTime,
} from '../../helpers';
import { useAllTimeStartDate } from '../../ReportsConfiguration/useReportsConfiguration';
import { usePerformanceReportContext } from '../context/PerformanceReportContext';

const formatPercentageForChip = (
  current: number | null,
  past: number | null,
) => {
  if (past === null || current === null) return null;

  if (past === 0 && current === 0) return 0;

  if (past === 0) return 'New';

  return Math.round(((current - past) / past) * 100);
};

export const useComparisonView = ({
  viewReportAs,
}: {
  viewReportAs: string;
}) => {
  const { filters } = usePerformanceReportContext();
  const allTimeStartDate = useAllTimeStartDate();
  const query = buildReportingSummaryPerformanceQuery(
    filters,
    viewReportAs,
    allTimeStartDate,
  );

  const currentDate = new Date();
  const isCurrentYear =
    filters?.reportingPeriod &&
    filters.reportingPeriod === currentDate.getFullYear();
  const isAllTime = isReportingPeriodAllTime(filters?.reportingPeriod);

  const { data, isLoading } = usePayment('/reporting/summary', {
    params: {
      query: {
        ...query,
        limit: 1,
        offset: 0,
      },
    },
  });

  const { closingDateEnd, closingDateStart } = getPriorReportingPeriodDateRange(
    filters?.reportingPeriod,
    { start: filters?.customDateStart, end: filters?.customDateEnd },
  );

  // If we're comparing against the current year, we need to compare against just the previous year data that matches
  // the current year's date. For example, if the current date is March 13, 2026, we need to compare
  // against the previous year's data up until March 13 (March 13, 2025).
  const priorYearClosingDateEnd = isCurrentYear
    ? format(subYears(currentDate, 1), 'yyyy-MM-dd')
    : closingDateEnd;

  const { data: priorYearData, isLoading: isPriorYearDataLoading } = usePayment(
    '/reporting/summary',
    isAllTime
      ? null
      : {
          params: {
            query: {
              ...query,
              limit: 1,
              offset: 0,
              closingDateStart,
              closingDateEnd: priorYearClosingDateEnd,
            },
          },
        },
  );

  const results = useMemo(() => {
    const past = priorYearData?.results?.[0];
    const current = data?.results?.[0];

    return [
      {
        id: 'sales-volume',
        label: 'Sales Volume',
        past: formatAbbreviatedPrice(past?.salesVolume ?? 0),
        current: formatAbbreviatedPrice(current?.salesVolume ?? 0),
        percentChange: formatPercentageForChip(
          current?.salesVolume ?? 0,
          past?.salesVolume ?? 0,
        ),
      },
      {
        id: 'adjusted-gci',
        label: 'Post-Referral GCI',
        past: formatAbbreviatedPrice(past?.adjustedGrossCommission ?? 0),
        current: formatAbbreviatedPrice(current?.adjustedGrossCommission ?? 0),
        percentChange: formatPercentageForChip(
          current?.adjustedGrossCommission ?? 0,
          past?.adjustedGrossCommission ?? 0,
        ),
      },
      {
        id: 'total-transactions',
        label: 'Total Transactions',
        past: past?.sides ?? 0,
        current: current?.sides ?? 0,
        percentChange: formatPercentageForChip(
          current?.sides ?? 0,
          past?.sides ?? 0,
        ),
      },
      {
        id: 'average-sale-value',
        label: 'Average Sale Value',
        past: formatAbbreviatedPrice(past?.averageSalesValue ?? 0),
        current: formatAbbreviatedPrice(current?.averageSalesValue ?? 0),
        percentChange: formatPercentageForChip(
          current?.averageSalesValue ?? 0,
          past?.averageSalesValue ?? 0,
        ),
      },
      {
        id: 'average-commission',
        label: 'Average Commission %',
        past: formatPercentage(past?.averageCommissionPercentage ?? 0),
        current: formatPercentage(current?.averageCommissionPercentage ?? 0),
        percentChange: formatPercentageForChip(
          current?.averageCommissionPercentage ?? 0,
          past?.averageCommissionPercentage ?? 0,
        ),
      },
    ];
  }, [priorYearData, data]);

  const { currentPeriodLabel, pastPeriodLabel } = useMemo(() => {
    const { closingDateStart: currentStart, closingDateEnd: currentEnd } =
      getReportingPeriodDateRange(
        filters?.reportingPeriod,
        {
          start: filters?.customDateStart,
          end: filters?.customDateEnd,
        },
        allTimeStartDate,
      );

    return {
      currentPeriodLabel: formatDateRange(currentStart, currentEnd),
      pastPeriodLabel: isAllTime
        ? ''
        : formatDateRange(closingDateStart, priorYearClosingDateEnd),
    };
  }, [
    filters?.reportingPeriod,
    filters?.customDateStart,
    filters?.customDateEnd,
    allTimeStartDate,
    isAllTime,
    closingDateStart,
    priorYearClosingDateEnd,
  ]);

  return {
    data: results,
    isLoading: isLoading || isPriorYearDataLoading,
    currentPeriodLabel,
    pastPeriodLabel,
  };
};

function formatShortDate(dateStr: string): string {
  return format(parseISO(dateStr), 'M/d/yy');
}

function formatDateRange(
  start: string | undefined,
  end: string | undefined,
): string {
  if (!start && !end) return '';
  if (!start) return formatShortDate(end!);
  if (!end) return formatShortDate(start);
  return `${formatShortDate(start)} – ${formatShortDate(end)}`;
}
